import isString from 'lodash/isString';
import { z } from 'zod';
import { Company } from '~/types/company';
import { Document, DocumentDirection, DocumentStatus, DocumentType } from '~/types/document';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { SortInfo } from '~/utils/jsonapi-query';
import { Account } from './account';
import { CategoryFilterValue } from './category';
import { Money } from './money';
import { Payment } from './payment';
import { Reconciliation, ReconciliationSource } from './reconciliation';
import { Transaction } from './transaction';
import { isDefined } from './typeGuards';

export const FlowResourceType = 'flow';

export const FlowsQueryKeys = {
  all: ['Flows'],
  byCompany: (companyId: string) => ['Flows', companyId],
  single: (id: Flow['id'] | null) => ['Flows', id],
  search: (companyId: string, filters: FetchFlowFilters, sorting: SortInfo<FlowSorting>) => [
    'Flows',
    companyId,
    filters,
    sorting,
  ],
  searchMetadata: (companyId: string, filters: FetchFlowFilters) => ['Flows', companyId, 'Metadata', filters],
  recommendedTransactions: (companyId: string, id: Flow['id']) => ['Flows', companyId, 'Recommended', id],
  reconciliations: (companyId: string, id: Flow['id']) => ['Flows', companyId, 'Reconciliation', id],
  reconciliationsForced: (companyId: string, id: Flow['id']) => ['Flows', companyId, 'ReconciliationForced', id],
  page: (companyId: string, filters: FetchFlowFilters, sorting: SortInfo<FlowSorting>, pageSize: number) => [
    'Flows',
    companyId,
    'Page',
    filters,
    sorting,
    pageSize,
  ],
};

export enum EmailReminderType {
  Manual = 'MANUAL',
  Automatic = 'AUTOMATIC',
}

export interface EmailReminder {
  id: string;
  sentAt: string | null;
  reminderType: EmailReminderType;
}

export interface Flow {
  accountInstitution: string | null;
  id: string;
  expectedPaymentDate: string;
  paymentAccount: string | null;
  paymentDate: string;
  paymentMethod: PaymentMethod | null;
  payeeName: string | null;
  paymentStatus: FlowPaymentStatus;
  extendedPaymentStatus?: FlowExtendedPaymentStatus;
  lastEmail: string | null;
  amount: Money;
  notes: string | null;
  document?: Document;
  company?: Company;
  account?: {
    id: string | null;
  } | null;
  emailReminders?: EmailReminder[];
  reconciliations?: Reconciliation[];
  payments?: Payment[];
  verificationStatus: VerificationStatus;
  uneditableReasons: FlowBlockReason[];
  undeletableReasons: FlowBlockReason[];
}

const FLOW_BLOCK_REASON_BOOKKEEPING_REGISTERED = 'flow_registered_in_bookkeeping';

type FlowBlockReason = typeof FLOW_BLOCK_REASON_BOOKKEEPING_REGISTERED;

export const isFlowBookkeepingRegistered = (flow: Pick<Flow, 'uneditableReasons' | 'undeletableReasons'>): boolean =>
  flow.uneditableReasons.includes(FLOW_BLOCK_REASON_BOOKKEEPING_REGISTERED) ||
  flow.undeletableReasons.includes(FLOW_BLOCK_REASON_BOOKKEEPING_REGISTERED);

export enum VerificationStatus {
  VERIFIED = 'VERIFIED',
  TO_VERIFY = 'TO_VERIFY',
}
export enum PaymentMethod {
  Transfer = 'TRANSFER',
  Cash = 'CASH',
  Card = 'CARD',
  Check = 'CHECK',
  Postal = 'POSTAL',
  Riba = 'RIBA',
  Sdd = 'SDD',
  FiscalCredit = 'FISCAL_CREDIT',
  Deferred = 'DEFERRED',
  TaxForm = 'TAX_FORM',
  Other = 'OTHER',
}

export enum FlowPaymentStatus {
  Paid = 'PAID',
  ToPay = 'TO_PAY',
}

export enum FlowExtendedPaymentStatus {
  Paid = 'PAID',
  PaymentSent = 'PAYMENT_SENT',
  ToPay = 'TO_PAY',
}

export interface FlowWithDocument extends Flow {
  document: Document;
}

export interface FlowCreate {
  paymentDate: Date | null;
  expectedPaymentDate: Date | null;
  paymentStatus: FlowPaymentStatus;
  paymentMethod: PaymentMethod | null;
  paymentAccount: string | null;
  payeeName?: string | null;
  amount: Money;
  notes?: string | null;
  document: {
    id: string;
  };
  account: {
    id: string;
  } | null;
}

export interface UpdateFlowValues {
  account?: { id: string } | null;
  amount?: Money;
  expectedPaymentDate?: Date;
  paymentAccount?: string | null;
  paymentDate?: Date;
  paymentMethod?: PaymentMethod | null;
  paymentStatus?: FlowPaymentStatus;
  notes?: string | null;
  verificationStatus?: VerificationStatus | null;
}

export interface FlowsMeta {
  page: MetaDataCursorPage;
}

export type FlowStatusFilterValue = FlowPaymentStatus | DocumentStatus.Draft;

export interface FetchFlowFilters {
  companyId?: string;
  search?: string;
  isInflow?: boolean | null;
  documentDirection?: DocumentDirection | null;
  documentNumber?: string | null;
  counterpartId?: string | null;
  customCounterpartId?: string | null;
  paymentStatus?: FlowPaymentStatus | null;
  extendedPaymentStatus?: FlowExtendedPaymentStatus | null;
  paymentMethod?: PaymentMethod[];
  documentType?: DocumentType[];
  documentStatus?: DocumentStatus | null;
  dateRange?: [string | null, string | null];
  documentFlowExpectedPaymentDateRange?: [string | null, string | null];
  ids?: Flow['id'][];
  excludeIds?: Flow['id'][];
  accountId?: string[] | null;
  category?: CategoryFilterValue | null;
  excludePaymentMethod?: PaymentMethod[];
  verificationStatus?: VerificationStatus | null;
  withoutBankIdentifier?: boolean;
}

export interface DownloadXMLBody {
  accountId: string;
  companyId: string;
  sepaInitiatingPartyIdentifier?: string;
  groupByCounterpart: boolean;
  /** When true the payment is requested as instant (SEPA Instant). */
  fast?: boolean;
  /** Requested execution date (yyyy-MM-dd). Omitted for instant payments. */
  paymentDate?: string;
  flows: {
    id: string;
    paymentAccount: string;
    counterpartName: string;
    documentNumber: string;
    /** Optional custom SEPA Ustrd. Only honored when `groupByCounterpart` is true. */
    remittanceMessage?: string;
  }[];
}

export type FlowSorting = 'expectedPaymentDate' | 'amount';

export interface FlowsFiltersFields {
  search: string;
  dateRange: [string | null, string | null];
  documentDirection?: DocumentDirection | null;
  isInflow: boolean | '';
  paymentStatus: FlowStatusFilterValue | null;
  extendedPaymentStatus: FlowExtendedPaymentStatus | null;
  accountId: string[] | null;
  paymentMethod: PaymentMethod[];
  documentType: DocumentType[];
  category: CategoryFilterValue | null;
  verificationStatus: VerificationStatus | null;
}

export interface FilterPreset {
  id: string;
  name: string;
  nameKey?: string;
  filters: Partial<FlowsFiltersFields>;
  quickFilterKey?: string;
}

export interface OutstandingCustomFiltersBlob {
  version: number;
  inflow: FilterPreset[];
  outflow: FilterPreset[];
  all: FilterPreset[];
}

export interface FlowPatchForm {
  expectedPaymentDate: Date;
  paymentStatus: FlowPaymentStatus;
  totalAmount: string;
  paymentMethod: PaymentMethod;
  paymentAccount: string;
  currency: string;
  account: {
    id: string | null;
  };
  notes: string;
}

export const flowPatchFormSchema = z.object({
  expectedPaymentDate: z.date(),
  paymentStatus: z.nativeEnum(FlowPaymentStatus),
  totalAmount: z.string(),
  paymentMethod: z.nativeEnum(PaymentMethod),
  currency: z.string(),
  paymentAccount: z.string(),
  account: z.object({
    id: z.string().nullable(),
  }),
  notes: z.string(),
});

export interface FlowsTotals {
  total: number;
  totalsMoney: { currency: string; negative: Money; positive: Money }[];
  totalsEur: { currency: string; negative: Money; positive: Money };
  hasBookkeepingRegistration: boolean;
}

export interface SplitPaymentNewFlowRequest {
  newFlowAmount: Money;
}

export interface SplitPaymentMoveToExistingRequest {
  amountToMove: Money;
  destinationFlowId: Flow['id'];
}

export const isFlowPaymentStatus = (val: unknown): val is FlowPaymentStatus => {
  if (!isDefined(val)) return false;
  if (!isString(val)) return false;
  return Object.values(FlowPaymentStatus)
    .map((el) => el.toString())
    .includes(val);
};

export enum FlowPaymentTerms {
  Plus15 = '15',
  Plus30 = '30',
  Plus60 = '60',
  Plus90 = '90',
  Plus120 = '120',
  Plus150 = '150',
  EOM = 'EOM',
  Plus30EOM = '30EOM',
  Plus60EOM = '60EOM',
  Plus90EOM = '90EOM',
  Plus120EOM = '120EOM',
  Plus150EOM = '150EOM',
  EOMPlus15 = 'EOMPLUS15',
  Plus30EOMPlus15 = '30EOMPLUS15',
  Plus60EOMPlus15 = '60EOMPLUS15',
  Plus90EOMPlus15 = '90EOMPLUS15',
  Plus120EOMPlus15 = '120EOMPLUS15',
}

type SplitPaymentOption =
  | {
      type: 'new-flow';
    }
  | {
      type: 'move-to-existing';
      flow: Flow;
    };

interface SelectionItem {
  transaction: Transaction;
  source: ReconciliationSource;
  splitPayment?: SplitPaymentOption;
  amountToReconcile?: Money;
}

export interface FlowReconciliationState {
  selected: SelectionItem;
  toSplit?: Money;
}

export type BulkUpdateFlowPayload =
  | { paymentStatus: FlowPaymentStatus }
  | { accountId: Account['id'] | null }
  | { verificationStatus: VerificationStatus }
  | {
      expectedPaymentDate: Flow['expectedPaymentDate'];
    };
